import React, {useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";
import {InputTextarea} from "primereact/inputtextarea";
import {Message} from "primereact/message";
import {ProgressSpinner} from "primereact/progressspinner";
import {SelectButton} from "primereact/selectbutton";
import {Tag} from "primereact/tag";
import PrimeDatePicker from "../../../system/components/prime-date-picker";
import {hasAdminPermission} from "../../../system/admin-access";
import {adjustSubscription, getSubscriptionAdjustment} from "../../../system/support-access-api";
import {useTranslate} from "../../../system/useTranslate";
import {FormField, RTL_DROPDOWN_PROPS} from "./common";

// «مدیریت اشتراک»: support changes a workspace's remaining time, plan and extra member capacity
// (zofino-strapi src/billing/subscription-admin.ts; docs/tasks/40-subscription-time-and-admin-edit.md in zofino-react).
const DAY = 24 * 60 * 60 * 1000;
const faDate = (value) => {
    if (!value) return "—";
    try {
        return new Intl.DateTimeFormat("fa-IR-u-ca-persian", {year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Tehran"}).format(new Date(value));
    } catch {
        return String(value);
    }
};
const faNumber = (value) => new Intl.NumberFormat("fa-IR").format(value);

function remaining(endValue, translate) {
    const end = Date.parse(endValue || "");
    if (!Number.isFinite(end)) return null;
    const ms = end - Date.now();
    if (ms <= 0) return translate("billing-admin-adjust-ended");
    const hours = Math.ceil(ms / 3600000);
    return hours >= 24
        ? translate("billing-admin-adjust-days-left").replace("{{n}}", faNumber(Math.floor(hours / 24)))
        : translate("billing-admin-adjust-hours-left").replace("{{n}}", faNumber(hours));
}

export default function SubscriptionAdjustDialog({documentId, visible, onHide, onSaved}) {
    const translate = useTranslate();
    const {user} = useSelector((state) => state.auth);
    const canChange = hasAdminPermission(user, "subscriptions.change");
    const canOverride = hasAdminPermission(user, "subscriptions.override");
    const [view, setView] = useState(null);
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const [mode, setMode] = useState("days");
    const [addDays, setAddDays] = useState(null);
    const [endSeconds, setEndSeconds] = useState(null);
    const [plan, setPlan] = useState(null);
    const [extraMembers, setExtraMembers] = useState(null);
    const [reason, setReason] = useState("");

    useEffect(() => {
        if (!visible || !documentId) return;
        setView(null);
        setError("");
        setMode("days");
        setAddDays(null);
        setEndSeconds(null);
        setReason("");
        getSubscriptionAdjustment(documentId)
            .then((next) => {
                setView(next);
                setPlan(next?.plan?.documentId || null);
                setExtraMembers(next?.extraMembers ?? 0);
            })
            .catch((requestError) => setError(translate(requestError?.response?.data?.error?.message || "billing-admin-adjust-error-load")));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, documentId]);

    const currentEnd = view ? (view.subscriptionStatus === "trialing" ? view.trialEndsAt || view.currentPeriodEnd : view.currentPeriodEnd) : null;
    // the end date the change would give (the server computes the same and checks it again)
    const newEnd = useMemo(() => {
        if (mode === "days" && addDays) {
            const current = Date.parse(currentEnd || "");
            return new Date((Number.isFinite(current) && current > Date.now() ? current : Date.now()) + addDays * DAY).toISOString();
        }
        if (mode === "date" && endSeconds) return new Date(endSeconds * 1000).toISOString();
        return null;
    }, [mode, addDays, endSeconds, currentEnd]);

    const change = {
        ...(newEnd ? (mode === "days" ? {addDays} : {periodEnd: newEnd}) : {}),
        ...(view && plan && plan !== view.plan?.documentId ? {planDocumentId: plan} : {}),
        ...(view && extraMembers !== null && extraMembers !== view.extraMembers ? {extraMembers} : {}),
    };
    const hasChange = Object.keys(change).length > 0;
    const newLimit = view && view.membersLimit !== null && view.membersLimit !== undefined && extraMembers !== null
        ? view.membersLimit - (view.extraMembers || 0) + extraMembers
        : null;

    const save = async () => {
        setSaving(true);
        setError("");
        try {
            const next = await adjustSubscription(documentId, {...change, reason});
            setView(next);
            onSaved?.(next);
            onHide();
        } catch (requestError) {
            setError(translate(requestError?.response?.data?.error?.message || "billing-admin-adjust-error-save"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog visible={visible} onHide={onHide} className="rtl w-11 md:w-8 lg:w-6 xl:w-5" header={<span className="text-sm">{translate("billing-admin-adjust-title")}</span>}
                headerClassName="border-bottom-1 border-200 py-3" contentClassName="p-4"
                footer={(
                    <div className="flex gap-2 justify-content-end pt-3">
                        <Button type="button" text className="text-xs" label={translate("cancel")} onClick={onHide}/>
                        <Button type="button" className="text-xs gap-2" icon="pi pi-check" label={translate("billing-admin-adjust-save")} loading={saving}
                                disabled={!view || !hasChange || reason.trim().length < 3} onClick={save}/>
                    </div>
                )}>
            {!view && !error && <div className="flex justify-content-center p-4"><ProgressSpinner strokeWidth="4" className="w-3rem h-3rem"/></div>}
            {error && <Message severity="error" className="w-full justify-content-start text-xs mb-3" text={error}/>}
            {view && (
                <div className="flex flex-column gap-4">
                    <div className="surface-50 border-round-lg p-3 flex flex-column gap-2 text-xs">
                        <div className="flex flex-wrap align-items-center gap-2">
                            <strong className="text-sm">{view.workspace?.name}</strong>
                            <Tag className="text-xs" value={view.plan?.name || view.plan?.code || "—"}/>
                            <Tag className="text-xs" severity={["active", "trialing"].includes(view.subscriptionStatus) ? "success" : "warning"} value={translate(`sales-status-${String(view.subscriptionStatus).replace(/_/g, "-")}`)}/>
                        </div>
                        <span>{translate("billing-admin-adjust-current-end")}: <strong>{faDate(currentEnd)}</strong>{remaining(currentEnd, translate) ? ` (${remaining(currentEnd, translate)})` : ""}</span>
                        <span>{translate("billing-admin-adjust-members")}: <strong>{faNumber(view.membersUsed || 0)}</strong> / {view.membersLimit === null || view.membersLimit === undefined ? translate("billing-admin-adjust-unlimited") : faNumber(view.membersLimit)}
                            {view.extraMembers ? ` (${translate("billing-admin-adjust-extra-included").replace("{{n}}", faNumber(view.extraMembers))})` : ""}</span>
                    </div>

                    <div className="flex flex-column gap-2">
                        <strong className="text-xs">{translate("billing-admin-adjust-time")}</strong>
                        <SelectButton className="ltr flex text-xs" pt={{button: () => ({className: "flex-1 text-xs py-2"})}} value={mode} allowEmpty={false} disabled={!canChange}
                                      options={[{value: "date", label: translate("billing-admin-adjust-mode-date")}, {value: "days", label: translate("billing-admin-adjust-mode-days")}]}
                                      onChange={(event) => setMode(event.value)}/>
                        <div className="grid m-0">
                            {mode === "days"
                                ? <FormField label={translate("billing-admin-adjust-add-days")} hint={translate("billing-admin-adjust-add-days-hint")} className="col-12 md:col-6 p-1">
                                    <InputNumber value={addDays} onValueChange={(event) => setAddDays(event.value)} min={-3650} max={3650} showButtons disabled={!canChange} inputClassName="w-full text-xs" placeholder="30"/>
                                </FormField>
                                : <FormField label={translate("billing-admin-adjust-end-date")} hint={translate("billing-admin-adjust-end-date-hint")} className="col-12 md:col-6 p-1">
                                    <PrimeDatePicker inputClassName="w-full text-xs" value={endSeconds} onChange={(event) => setEndSeconds(event.value)}/>
                                </FormField>}
                            <div className="col-12 md:col-6 p-1 flex align-items-center text-xs text-600">
                                {newEnd ? <span>{translate("billing-admin-adjust-new-end")}: <strong className="text-900">{faDate(newEnd)}</strong></span> : null}
                            </div>
                        </div>
                    </div>

                    <div className="grid m-0">
                        <FormField label={translate("billing-admin-adjust-plan")} hint={translate("billing-admin-adjust-plan-hint")} className="col-12 md:col-6 p-1">
                            <Dropdown {...RTL_DROPDOWN_PROPS} value={plan} disabled={!canChange} options={(view.plans || []).map((item) => ({value: item.documentId, label: item.name || item.code}))}
                                      onChange={(event) => setPlan(event.value)}/>
                        </FormField>
                        <FormField label={translate("billing-admin-adjust-extra-members")} hint={translate("billing-admin-adjust-extra-members-hint")} className="col-12 md:col-6 p-1">
                            <InputNumber value={extraMembers} onValueChange={(event) => setExtraMembers(event.value ?? 0)} min={0} max={1000} showButtons disabled={!canOverride} inputClassName="w-full text-xs"/>
                            {newLimit !== null && extraMembers !== view.extraMembers && (
                                <small className="text-xs text-primary">{translate("billing-admin-adjust-new-limit").replace("{{n}}", faNumber(newLimit))}</small>
                            )}
                        </FormField>
                    </div>

                    <FormField label={translate("billing-admin-adjust-reason")} required hint={translate("billing-admin-adjust-reason-hint")} className="col-12 p-0">
                        <InputTextarea className="w-full text-xs" rows={2} autoResize value={reason} onChange={(event) => setReason(event.target.value)}/>
                    </FormField>
                    <small className="text-xs text-500 line-height-3">{translate("billing-admin-adjust-notify-note")}</small>

                    {view.history?.length > 0 && (
                        <div className="flex flex-column gap-2">
                            <strong className="text-xs">{translate("billing-admin-adjust-history")}</strong>
                            {view.history.map((item, index) => (
                                <div key={index} className="surface-50 border-round-lg p-2 text-xs flex flex-column gap-1">
                                    <span className="text-500">{faDate(item.at)} · {item.by?.email || "—"}</span>
                                    <span>{item.reason}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </Dialog>
    );
}
